import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Package, MapPin, ClockCounterClockwise, HandArrowDown, ArrowUUpLeft, Share,
  CheckCircle, WarningCircle, GearSix,
} from "@phosphor-icons/react";

const OWNERSHIP = [
  ["company", "Company owned"], ["employee", "Employee owned"], ["subcontractor", "Subcontractor owned"],
  ["hired", "Hired / leased"], ["client", "Client owned"], ["other", "Other"], ["unknown", "Unknown / not confirmed"],
];
const OWNERSHIP_LABEL = Object.fromEntries(OWNERSHIP);

const STATE_STYLE = {
  recently_confirmed: { c: "bg-green-100 text-green-800", t: "Recently confirmed" },
  stale: { c: "bg-amber-100 text-amber-800", t: "Location unverified" },
  discrepancy: { c: "bg-red-100 text-red-800", t: "Discrepancy reported" },
  unconfirmed: { c: "bg-slate-100 text-slate-600", t: "Not yet confirmed" },
};

function StateChip({ state }) {
  const s = STATE_STYLE[state] || STATE_STYLE.unconfirmed;
  return <span data-testid="loc-state" className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.c}`}>{s.t}</span>;
}

function NestedContents({ nodes }) {
  const rows = [];
  const walk = (list, depth) => list.forEach((n) => { rows.push({ n, depth }); if (n.contents && n.contents.length) walk(n.contents, depth + 1); });
  walk(nodes, 0);
  return (
    <div className="flex flex-col gap-1 pl-3">
      {rows.map(({ n, depth }) => {
        const c = n.effective_custodian;
        return (
          <a key={n.id} href={`/assets/${n.id}`} data-testid={`myeq-nested-${n.id}`}
            style={{ paddingLeft: depth * 14 }}
            className="flex items-center gap-2 text-sm border-l-2 border-slate-200 pl-2 py-1 hover:bg-slate-50 rounded">
            <Package size={14} className="text-slate-400 shrink-0" />
            <span className="min-w-0"><span className="font-mono text-xs text-slate-500">{n.asset_number}</span> <span className="font-semibold text-slate-700">{n.name}</span></span>
            {c && c.source !== "inherited" && c.name &&
              <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">{c.name}</span>}
          </a>
        );
      })}
    </div>
  );
}

function LastConfirmed({ item }) {  const lc = item.last_confirmed;
  return (
    <div className="flex items-center gap-2 flex-wrap text-sm text-slate-600">
      <MapPin size={16} className="text-slate-400" />
      <span>{lc?.label || item.home_location_name || "No location recorded"}</span>
      {lc?.days_ago != null && <span className="text-slate-400">· {lc.days_ago === 0 ? "today" : `${lc.days_ago}d ago`}</span>}
      <StateChip state={item.location_state} />
    </div>
  );
}

export default function MyEquipmentPage() {
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState([]);
  const [locations, setLocations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [history, setHistory] = useState(null);
  const [dialog, setDialog] = useState(null); // {kind, item}
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [tasks, setTasks] = useState([]);

  const isManager = me?.permissions?.some((p) => ["asset_manager", "administrator", "super_admin"].includes(p));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/equipment/my");
      setData(r.data);
    } catch (e) { toast.error("Could not load your equipment"); }
    setLoading(false);
  }, []);

  useEffect(() => {
    api.get("/me").then((r) => setMe(r.data)).catch(() => {});
    load();
    api.get("/people").then((r) => setPeople((r.data.items || r.data || []).filter((p) => p.is_active !== false))).catch(() => {});
    api.get("/config/locations").then((r) => setLocations(r.data.items || r.data || [])).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data.items || r.data || [])).catch(() => {});
    api.get("/equipment/stock-checks?mine=true").then((r) => setTasks(r.data || [])).catch(() => {});
  }, [load]);

  const refreshTasks = () => api.get("/equipment/stock-checks?mine=true").then((r) => setTasks(r.data || [])).catch(() => {});
  const markItem = async (itemId, state) => {
    try { await api.post(`/equipment/stock-checks/items/${itemId}`, { state }); refreshTasks(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const kitCheck = (item) => act(async () => {
    await api.post(`/equipment/kits/${item.id}/check`, { result: "all_ok" });
    toast.success("Kit confirmed — all present & compliant");
  });

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); setDialog(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };

  const confirmHave = (item) => act(async () => {
    await api.post(`/equipment/${item.id}/confirm-at`, {});
    toast.success("Confirmed");
  });
  const openHistory = async (item) => {
    setHistory({ item, rows: null });
    try { const r = await api.get(`/equipment/${item.id}/history`); setHistory({ item, rows: r.data }); }
    catch { setHistory({ item, rows: [] }); }
  };
  const submitReport = () => act(async () => {
    await api.post(`/equipment/${dialog.item.id}/report`, { kind: form.kind || "damage", note: form.note });
    toast.success(form.kind === "cannot_locate" ? "Reported — flagged for your manager" : "Damage reported");
  });
  const submitLoan = () => act(async () => {
    if (!form.loaned_to_id) throw { response: { data: { detail: "Choose who to loan it to" } } };
    await api.post(`/equipment/${dialog.item.id}/loan`, { loaned_to_id: form.loaned_to_id, expected_return_date: form.expected_return_date || null, note: form.note });
    toast.success("Item loaned out");
  });
  const returnLoan = (item) => act(async () => {
    await api.post(`/equipment/loans/${item.loan.id}/return`, {});
    toast.success("Item returned");
  });
  const submitShare = () => act(async () => {
    if (!form.profile_id) throw { response: { data: { detail: "Choose someone to share with" } } };
    await api.post(`/equipment/${dialog.item.id}/share`, { profile_id: form.profile_id });
    toast.success("Shared");
  });
  const submitManage = () => act(async () => {
    await api.post(`/equipment/${dialog.item.id}/manage`, form);
    toast.success("Equipment updated");
  });

  const openDialog = (kind, item) => { setForm(kind === "manage" ? {
    ownership_type: item.ownership_type, owner: item.owner_text || "",
    assigned_person_id: item.custodian_id || "", team_id: item.team_id || "", home_location_id: item.home_location_id || "",
  } : {}); setDialog({ kind, item }); };

  const Card = ({ item, context }) => (
    <div data-testid={`equip-${item.asset_number}`} className="bg-white border-2 border-slate-200 rounded-xl p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-slate-900">{item.name}</span>
            <span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{item.asset_number}</span>
          </div>
          <div className="text-sm text-slate-500 mt-0.5">
            {item.asset_type_name || "Equipment"} · <span className="font-semibold">{OWNERSHIP_LABEL[item.ownership_type] || "Company owned"}</span>
            {item.custodian_name && <> · Held by {item.custodian_name}</>}
          </div>
        </div>
        {item.blocks_use && <span className="text-xs font-bold text-red-700 bg-red-50 px-2 py-1 rounded shrink-0">{item.status_name}</span>}
      </div>
      <LastConfirmed item={item} />
      {item.loan && context !== "loaned_out" && (
        <div className="text-sm text-blue-700 bg-blue-50 rounded px-2 py-1">On loan from {item.loan.loaned_by_name}</div>
      )}
      {item.loan && context === "loaned_out" && (
        <div className="text-sm text-blue-700 bg-blue-50 rounded px-2 py-1">Loaned to {item.loan.loaned_to_name}{item.loan.expected_return_date ? ` · due ${item.loan.expected_return_date}` : ""}</div>
      )}
      {item.attention_reasons?.length > 0 && (
        <div className="text-sm text-amber-800 bg-amber-50 rounded px-2 py-1">{item.attention_reasons.join(" · ")}</div>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        {(context === "loaned_to_me" || context === "loaned_out") ? (
          <Button size="sm" data-testid={`return-${item.asset_number}`} onClick={() => returnLoan(item)} className="bg-slate-900"><ArrowUUpLeft size={16} className="mr-1" />Return</Button>
        ) : (
          <Button size="sm" data-testid={`confirm-${item.asset_number}`} onClick={() => confirmHave(item)} className="bg-green-600 hover:bg-green-700"><CheckCircle size={16} className="mr-1" />Confirm I have it</Button>
        )}
        <Button size="sm" variant="outline" data-testid={`report-${item.asset_number}`} onClick={() => openDialog("report", item)}><WarningCircle size={16} className="mr-1" />Report</Button>
        {(context === "assigned" || isManager) && !item.loan && (
          <Button size="sm" variant="outline" data-testid={`loan-${item.asset_number}`} onClick={() => openDialog("loan", item)}><HandArrowDown size={16} className="mr-1" />Loan</Button>
        )}
        {(context === "assigned" || isManager) && (
          <Button size="sm" variant="outline" data-testid={`share-${item.asset_number}`} onClick={() => openDialog("share", item)}><Share size={16} className="mr-1" />Share</Button>
        )}
        <Button size="sm" variant="ghost" data-testid={`history-${item.asset_number}`} onClick={() => openHistory(item)}><ClockCounterClockwise size={16} className="mr-1" />History</Button>
        {item.asset_group === "kit" && (
          <Button size="sm" variant="outline" data-testid={`kitcheck-${item.asset_number}`} onClick={() => kitCheck(item)} className="border-2">Contents OK</Button>
        )}
        {isManager && (
          <Button size="sm" variant="ghost" data-testid={`manage-${item.asset_number}`} onClick={() => openDialog("manage", item)}><GearSix size={16} className="mr-1" />Manage</Button>
        )}
      </div>
    </div>
  );

  const Section = ({ title, items, context, tone }) => {
    if (!items?.length) return null;
    return (
      <div className="flex flex-col gap-3">
        <h2 className={`font-display text-lg font-bold ${tone || "text-slate-800"}`}>{title} <span className="text-slate-400 font-normal">({items.length})</span></h2>
        <div className="grid gap-3 md:grid-cols-2">{items.map((it) => (
          <div key={it.id + context} className="flex flex-col gap-1">
            <Card item={it} context={context} />
            {it.contents?.length > 0 && <NestedContents nodes={it.contents} />}
          </div>
        ))}</div>
      </div>
    );
  };

  const peopleOpts = people.filter((p) => p.id !== me?.id);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 flex flex-col gap-8" data-testid="my-equipment-page">
      <div className="flex items-center gap-3">
        <Package size={30} weight="bold" className="text-blue-600" />
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">My Equipment</h1>
          <p className="text-sm text-slate-500">Confirm, check, loan and track your equipment.</p>
        </div>
      </div>

      {tasks.length > 0 && (
        <div className="flex flex-col gap-3" data-testid="my-check-tasks">
          <h2 className="font-display text-lg font-bold text-blue-700">Check tasks for me ({tasks.length})</h2>
          {tasks.map((t) => (
            <div key={t.id} className="bg-white border-2 border-blue-200 rounded-xl p-4 flex flex-col gap-2" data-testid={`task-${t.id}`}>
              <div className="flex justify-between"><span className="font-semibold">{t.target_label || t.target_type} · {t.request_type} check</span><span className="text-sm text-slate-500">{t.progress.done}/{t.progress.total} · {t.status}</span></div>
              {t.items.map((it) => (
                <div key={it.id} className="flex items-center justify-between text-sm border-t pt-2">
                  <span>{it.label} {it.state !== "pending" && <em className="text-slate-400">· {it.state}</em>}</span>
                  <span className="flex gap-1">
                    <button data-testid={`task-present-${it.id}`} onClick={() => markItem(it.id, "present")} className="text-green-700 font-semibold px-2">Present</button>
                    <button data-testid={`task-missing-${it.id}`} onClick={() => markItem(it.id, "missing")} className="text-red-700 font-semibold px-2">Missing</button>
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {loading ? <p className="text-slate-500">Loading…</p> : !data ? null : (
        (data.assigned.length + data.shared_with_me.length + data.loaned_to_me.length + data.loaned_out.length) === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-10 text-center text-slate-500" data-testid="equip-empty">
            No equipment is currently assigned to, shared with, or loaned to you.
          </div>
        ) : (
          <>
            <Section title="Needs attention" items={data.needs_attention} context="assigned" tone="text-amber-700" />
            <Section title="My equipment" items={data.assigned} context="assigned" />
            <Section title="Shared with me" items={data.shared_with_me} context="shared" />
            <Section title="Loaned to me" items={data.loaned_to_me} context="loaned_to_me" />
            <Section title="Loaned out" items={data.loaned_out} context="loaned_out" />
          </>
        )
      )}

      {/* Report dialog */}
      <Dialog open={dialog?.kind === "report"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Report a problem</DialogTitle>
            <DialogDescription>{dialog?.item?.name}</DialogDescription></DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <Button data-testid="report-damage" variant={form.kind !== "cannot_locate" ? "default" : "outline"} onClick={() => setForm({ ...form, kind: "damage" })} className="flex-1">Damage / problem</Button>
              <Button data-testid="report-cannot-locate" variant={form.kind === "cannot_locate" ? "default" : "outline"} onClick={() => setForm({ ...form, kind: "cannot_locate" })} className="flex-1">Cannot locate</Button>
            </div>
            <Input data-testid="report-note" placeholder="Optional note" value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            <Button data-testid="report-submit" disabled={busy} onClick={submitReport} className="bg-slate-900">Submit report</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Loan dialog */}
      <Dialog open={dialog?.kind === "loan"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Loan item</DialogTitle>
            <DialogDescription>{dialog?.item?.name} — the item stays yours; this records who has it.</DialogDescription></DialogHeader>
          <div className="flex flex-col gap-3">
            <select data-testid="loan-person" className="h-11 border-2 rounded-md px-2" value={form.loaned_to_id || ""} onChange={(e) => setForm({ ...form, loaned_to_id: e.target.value })}>
              <option value="">Loan to…</option>
              {peopleOpts.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
            </select>
            <label className="text-sm text-slate-600">Expected return (optional)</label>
            <Input data-testid="loan-return-date" type="date" value={form.expected_return_date || ""} onChange={(e) => setForm({ ...form, expected_return_date: e.target.value })} />
            <Button data-testid="loan-submit" disabled={busy} onClick={submitLoan} className="bg-slate-900">Loan out</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={dialog?.kind === "share"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Share item</DialogTitle>
            <DialogDescription>{dialog?.item?.name} — lets someone see/use/check it without changing who holds it.</DialogDescription></DialogHeader>
          <div className="flex flex-col gap-3">
            {(dialog?.item?.shares?.length > 0) && <div className="text-sm text-slate-600">Shared with: {dialog.item.shares.map((s) => s.name).join(", ")}</div>}
            <select data-testid="share-person" className="h-11 border-2 rounded-md px-2" value={form.profile_id || ""} onChange={(e) => setForm({ ...form, profile_id: e.target.value })}>
              <option value="">Share with…</option>
              {peopleOpts.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
            </select>
            <Button data-testid="share-submit" disabled={busy} onClick={submitShare} className="bg-slate-900">Share</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage dialog (managers) */}
      <Dialog open={dialog?.kind === "manage"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Manage equipment</DialogTitle>
            <DialogDescription>{dialog?.item?.name}</DialogDescription></DialogHeader>
          <div className="flex flex-col gap-3">
            <label className="text-sm font-semibold text-slate-700">Ownership</label>
            <select data-testid="manage-ownership" className="h-11 border-2 rounded-md px-2" value={form.ownership_type || "company"} onChange={(e) => setForm({ ...form, ownership_type: e.target.value })}>
              {OWNERSHIP.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Input data-testid="manage-owner" placeholder="Named owner (e.g. subcontractor name)" value={form.owner || ""} onChange={(e) => setForm({ ...form, owner: e.target.value })} />
            <label className="text-sm font-semibold text-slate-700">Primary custodian</label>
            <select data-testid="manage-custodian" className="h-11 border-2 rounded-md px-2" value={form.assigned_person_id || ""} onChange={(e) => setForm({ ...form, assigned_person_id: e.target.value })}>
              <option value="">— none —</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
            </select>
            <label className="text-sm font-semibold text-slate-700">Home / expected location</label>
            <select data-testid="manage-home" className="h-11 border-2 rounded-md px-2" value={form.home_location_id || ""} onChange={(e) => setForm({ ...form, home_location_id: e.target.value })}>
              <option value="">— none —</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <label className="text-sm font-semibold text-slate-700">Team</label>
            <select data-testid="manage-team" className="h-11 border-2 rounded-md px-2" value={form.team_id || ""} onChange={(e) => setForm({ ...form, team_id: e.target.value })}>
              <option value="">— none —</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <Button data-testid="manage-submit" disabled={busy} onClick={submitManage} className="bg-slate-900">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History drawer */}
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Item history</DialogTitle>
            <DialogDescription>{history?.item?.name} · {history?.item?.asset_number}</DialogDescription></DialogHeader>
          {history?.rows == null ? <p className="text-slate-500">Loading…</p> :
            history.rows.length === 0 ? <p className="text-slate-500">No activity recorded yet.</p> : (
            <ol className="flex flex-col gap-3" data-testid="history-list">
              {history.rows.map((h) => (
                <li key={h.id} className="border-l-2 border-slate-200 pl-3">
                  <div className="text-sm font-semibold text-slate-800 capitalize">{h.event_type.replace(/_/g, " ")}{h.place_label ? ` → ${h.place_label}` : ""}</div>
                  {h.note && <div className="text-sm text-slate-600">{h.note}</div>}
                  <div className="text-xs text-slate-400">{h.actor_name || "—"} · {h.at ? new Date(h.at).toLocaleString("en-GB") : ""}</div>
                </li>
              ))}
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
